import { useState } from 'react';

import {
  canEditFundPolicy,
  canManageEmployees,
  canReadOtherEmployees,
  type Role,
} from '../../core/policy/roles';
import { useFundPolicy } from '../../features/auth/application/useFundPolicy';
import { outstandingProfileFields } from '../../features/employees/application/profileForm';
import { useMyDependents } from '../../features/employees/application/useMyDependents';
import { useMyRecord } from '../../features/employees/application/useMyRecord';
import { formatDateAr } from '../../shared/format/dateFormat';
import { AppShell, type ShellTab } from '../../pages/AppShell';
import { OverviewPage } from '../../pages/OverviewPage';
import { ProfilePage } from '../../pages/ProfilePage';
import { Alert, Card } from '../../shared/components';

import { useServices } from '../providers/servicesContext';
import { DeclarationScreen } from './DeclarationScreen';
import { DependentsScreen } from './DependentsScreen';
import { DocumentsScreen } from './DocumentsScreen';
import { ProgressScreen } from './ProgressScreen';
import { RegistrationReviewScreen } from './RegistrationReviewScreen';
import { RolesScreen } from './RolesScreen';
import { SettingsScreen } from './SettingsScreen';
import styles from '../../pages/AuthPages.module.css';

/**
 * Everything behind a linked sign-in.
 *
 * Split from `App` so the composition root stays about *which* state someone is
 * in, and this file about what a linked employee can do (§5.1).
 */
export function SignedInApp({
  roles,
  branches,
  onSignOut,
  ownUid,
}: {
  readonly roles: readonly Role[];
  /** The signed-in account, so the permissions screen can refuse self-demotion. */
  readonly ownUid: string;
  readonly branches: readonly { readonly id: string; readonly nameAr: string }[];
  readonly onSignOut: () => void;
}) {
  const { employees, fundPolicy, dependents } = useServices();
  const myRecord = useMyRecord(employees, true);
  const { policy, errorAr: policyError, reload: reloadPolicy } = useFundPolicy(fundPolicy, true);
  const today = new Date().toISOString().slice(0, 10);

  // Loaded here rather than inside the documents screen: the checklist needs
  // the dependants, and fetching them again per tab would be a read for a list
  // already in memory.
  const myDependents = useMyDependents(
    dependents,
    policy,
    policy !== null,
    today,
    myRecord.record?.fullName ?? '',
  );

  const [activeTabId, setActiveTabId] = useState('overview');
  const record = myRecord.record;

  if (!record) {
    return (
      <div className={styles.page}>
        <Card>
          <p className={styles.body} aria-live="polite">
            {myRecord.errorAr ?? 'جارٍ تحميل بياناتك…'}
          </p>
        </Card>
      </div>
    );
  }

  const outstanding = outstandingProfileFields(record);
  const branchNames = Object.fromEntries(
    branches.map((branch) => [branch.id, branch.nameAr]),
  );

  const tabs: ShellTab[] = [
    { id: 'overview', labelAr: 'ملفي' },
    { id: 'profile', labelAr: 'بياناتي', badge: outstanding.length },
    { id: 'dependents', labelAr: 'المعالون' },
    { id: 'declaration', labelAr: 'الإقرار الصحي' },
    { id: 'documents', labelAr: 'الوثائق' },
  ];

  // The dashboard is offered only to roles that may read other employees at
  // all. The rules refuse the reads regardless; this stops the committee tab
  // appearing for someone who would only see errors behind it.
  if (canReadOtherEmployees(roles)) {
    tabs.push({ id: 'progress', labelAr: 'متابعة الاكتمال' });
  }

  // Approving a registration creates an account link, so the screen is offered
  // only to the roles the callable itself accepts.
  if (canManageEmployees(roles)) {
    tabs.push({ id: 'requests', labelAr: 'طلبات التسجيل' });
  }

  // Only a manager may write the policy — the rules say so, and offering the
  // screen to anyone else would be offering a form that always fails to save.
  if (canEditFundPolicy(roles)) {
    tabs.push({ id: 'settings', labelAr: 'إعدادات المراحل' });
    tabs.push({ id: 'permissions', labelAr: 'صلاحيات اللجنة' });
  }

  const active = tabs.some((tab) => tab.id === activeTabId) ? activeTabId : 'overview';

  const go = (id: string) => () => {
    setActiveTabId(id);
  };

  /*
   * What is left to do, in the order it should be done.
   *
   * Derived from the same records the sections themselves read, so the summary
   * cannot claim a section is finished while that section disagrees.
   */
  const dependentCount = myDependents.dependents.length;
  const expectedChildren = record.declaredChildrenCount ?? 0;

  const tasks = [
    {
      id: 'profile',
      labelAr: 'إكمال بياناتي',
      remainingAr: outstanding.length === 0 ? null : `ينقص: ${outstanding.join(' · ')}`,
      onGo: go('profile'),
    },
    {
      id: 'dependents',
      labelAr: 'إدخال المعالين',
      remainingAr:
        dependentCount >= expectedChildren && dependentCount > 0
          ? null
          : `أُدخل ${String(dependentCount)} من ${String(expectedChildren)} حسب سجلّك`,
      onGo: go('dependents'),
    },
    {
      id: 'documents',
      labelAr: 'رفع الوثائق',
      // The checklist itself is built from the policy and the dependants; this
      // says where to go, and that screen says exactly which files remain.
      remainingAr: 'راجع قائمة الوثائق المطلوبة',
      onGo: go('documents'),
    },
  ];

  /*
   * The section after this one, so a saved form leads somewhere.
   *
   * Taken from the tab list rather than written out again: adding a section in
   * one place and forgetting it in the other is how a wizard starts skipping a
   * step.
   */
  const nextTab = tabs[tabs.findIndex((tab) => tab.id === active) + 1];

  return (
    <AppShell
      tabs={tabs}
      activeTabId={active}
      employeeName={record.fullName}
      onSelectTab={setActiveTabId}
      onSignOut={onSignOut}
    >
      {active === 'overview' ? (
        <OverviewPage
          fullName={record.fullName}
          phaseAr="المرحلة الحالية: إدخال البيانات ورفع الوثائق"
          phaseNoteAr={
            policy && !policy.employeeSubmissionsEnabled
              ? 'الإدخال متوقف حاليًا بقرار من اللجنة.'
              : null
          }
          facts={[
            { labelAr: 'الرقم الوظيفي', valueAr: record.employeeNumber },
            {
              labelAr: 'الفرع',
              valueAr: branchNames[record.branchId ?? ''] ?? 'غير محدد',
            },
            { labelAr: 'رقم الجوال', valueAr: record.phone ?? 'غير مسجّل' },
            {
              labelAr: 'تاريخ الميلاد',
              valueAr: record.birthDate ? formatDateAr(record.birthDate) : 'غير مسجّل',
            },
            { labelAr: 'العنوان', valueAr: record.address ?? 'غير مسجّل' },
          ]}
          tasks={tasks}
        />
      ) : null}

      {active === 'profile' ? (
        <ProfilePage
          employeeNumber={record.employeeNumber}
          fullName={record.fullName}
          branchNameAr={branchNames[record.branchId ?? ''] ?? null}
          initialValues={{
            nationalId: record.nationalId ?? '',
            birthDate: record.birthDate ?? '',
            gender: record.gender ?? '',
            phone: record.phone ?? '',
            address: record.address ?? '',
          }}
          outstanding={outstanding}
          today={today}
          onSave={myRecord.save}
          onSignOut={onSignOut}
          errorAr={myRecord.errorAr}
          savedAr={myRecord.savedAr}
          nextStep={
            nextTab
              ? {
                  labelAr: nextTab.labelAr,
                  onGo: () => {
                    setActiveTabId(nextTab.id);
                  },
                }
              : null
          }
        />
      ) : null}

      {active === 'progress' ? (
        <ProgressScreen
          branchNames={branchNames}
          branches={branches}
          canCorrect={canManageEmployees(roles)}
        />
      ) : null}

      {active === 'requests' ? (
        <RegistrationReviewScreen branchNames={branchNames} />
      ) : null}

      {active === 'permissions' ? <RolesScreen ownUid={ownUid} /> : null}

      {/* The three policy-driven screens cannot render without the policy:
          which relations qualify and what the declaration asks both come from
          it, and guessing a default would quietly contradict the settings. */}
      {active !== 'overview' &&
      active !== 'profile' &&
      active !== 'progress' &&
      active !== 'requests' &&
      active !== 'permissions' ? (
        policy === null ? (
          <div className={styles.page}>
            <Card>
              {policyError === null ? (
                <p className={styles.body} aria-live="polite">
                  جارٍ تحميل إعدادات الصندوق…
                </p>
              ) : (
                <Alert severity="error" title="تعذّر التحميل">
                  {policyError}
                </Alert>
              )}
            </Card>
          </div>
        ) : active === 'dependents' ? (
          <DependentsScreen
            policy={policy}
            declaredChildrenCount={record.declaredChildrenCount ?? 0}
            today={today}
            employeeFullName={record.fullName}
          />
        ) : active === 'declaration' ? (
          <DeclarationScreen policy={policy} nowIso={new Date().toISOString()} />
        ) : active === 'settings' ? (
          <SettingsScreen policy={policy} onSaved={reloadPolicy} />
        ) : (
          <DocumentsScreen policy={policy} dependents={myDependents.dependents} />
        )
      ) : null}
    </AppShell>
  );
}
